import activityService from 'services/ActivityService';
import pathwayService from 'services/PathwayService';
import interactiveService from 'services/InteractiveService';
import componentService from 'services/ComponentService';
import uuid from 'uuid/v1';
import { getPluginSchema } from 'data/Plugin/actions';
import { selectPluginManifest } from 'domains/coursewareItem/selectors';
import { commandAuthorPluginGet } from 'containers/ConnectionManager/actions';
import { findPropertiesByScope } from '@pearsontechnology/aero-plugin-schema';

import coursewareTargetService from './CoursewareTargetService';
import * as coursewareTypes from '../constants/coursewareTypes';
import * as dispatcher from '../event/dispatch';
import ArbitrarySubscriber from '../event/ArbitrarySubscriber';
import { dispatch, getState } from '../store';
import {
  removeChild,
  initChildWithCallback,
  coursewareAncestry,
} from '../lifecycle';

class LifecycleServices {
  constructor({ context, id, coursewareType }) {
    this.context = context;
    this.id = id;
    this.coursewareType = coursewareType;
    this.dispatcher = dispatcher;

    this.lifecycleSubscriber = new ArbitrarySubscriber();
  }

  getContext() {
    return this.context;
  }

  getInstanceId() {
    return this.id;
  }

  dispatch(action) {
    return this.dispatcher.dispatch(action);
  }

  unsubscribeAll() {
    this.lifecycleSubscriber.unsubscribeAll();
  }

  subscribe(key, fn) {
    this.lifecycleSubscriber.subscribe(fn, key);
  }

  _getManifest(pluginSummary) {
    return new Promise((resolve) => {
      const existingManifest = selectPluginManifest(
        getState(),
        pluginSummary.pluginId,
        pluginSummary.version
      );
      if (existingManifest) {
        resolve(existingManifest);
      } else {
        dispatch(commandAuthorPluginGet(
          pluginSummary.pluginId,
          pluginSummary.version,
          (loadedManifest /* , pluginSchema */) => {
            resolve(loadedManifest);
          }
        ));
      }
    });
  }

  async _getChildType(childId) {
    if (activityService.has(childId)) {
      return coursewareTypes.ACTIVITY;
    } else if (interactiveService.has(childId)) {
      return coursewareTypes.INTERACTIVE;
    } else if (componentService.has(childId)) {
      return coursewareTypes.COMPONENT;
    }
    const childTarget = await coursewareTargetService.build().createTarget(childId);
    return childTarget.type;
  }

  async initChild(childId, childContext, respondToInitChildCallback) {    
    // Return for testing
    let getCoursewareFn;
    
    const childType = await this._getChildType(childId);

    switch (childType) {
      case coursewareTypes.ACTIVITY:
        getCoursewareFn = activityService.getActivity;
        break;
      case coursewareTypes.INTERACTIVE:
        getCoursewareFn = interactiveService.getInteractive;
        break;
      case coursewareTypes.COMPONENT:
        getCoursewareFn = componentService.getComponent;
        break;
      default:
        getCoursewareFn = activityService.getActivity;
        break;
    }



    return getCoursewareFn(childId)
      .then((courseware) =>
        this._getManifest(courseware.plugin)
      )
      .then((manifest) => {
        initChildWithCallback(
          this.id,
          childId,
          childType,
          manifest,
          childContext,
          respondToInitChildCallback
        );
      });
  }

  removeChild(childId) {
    removeChild(this.id, childId);
  }

  async createChild(pluginId, pluginVersion, initialPluginState, name, respondToCreateChildCallback) {

    if (this.coursewareType === coursewareTypes.INTERACTIVE) {
      const componentId = await componentService.createInteractiveComponent(
        this.id,
        pluginId,
        pluginVersion,
        initialPluginState,
      );
      const localRef = uuid();
      await this.appendChild({ itemId: componentId, localRef }, this.id);
      respondToCreateChildCallback({ childId: componentId, localRef });

      dispatch(getPluginSchema(pluginId, pluginVersion, async (schema) => {
        const learnerEditableProperties = findPropertiesByScope(schema);
        if (learnerEditableProperties.length) {
        // Register in the background since it's a slow request
          await interactiveService.authorStudentScopeRegister(
            this.id,
            componentId,
          );
        }
      }));
    } else {
      dispatch(getPluginSchema(pluginId, pluginVersion, async (pluginSchema) => {
        const newActivityConfig = { name };
        const activityId = await activityService.createActivity(
          pluginId,
          pluginVersion,
          pluginSchema,
          newActivityConfig,
          false,
        );
        respondToCreateChildCallback({ childId: activityId });
      }));
    }
  }
  async appendChild (childInfo, elementId)  {
    const { config, config: {components = []}} = await interactiveService.getInteractive(elementId);
    components.push(childInfo);
    await interactiveService.saveInteractiveConfig(elementId, { ...config, components});
  }

  async deleteChild(childId) {
    if (this.coursewareType === coursewareTypes.INTERACTIVE) {
      try {
        // Deregister component from activity.
        // This deregister is required because some component's scope are uplifted to the Activity's scope
        // Uplifting are done for components that have scope bindings
        // ie have other courseware elements that depends it for data
        const interactive = await interactiveService.getInteractive(this.id);
        const pathway = await pathwayService.getPathway(interactive?.parentPathwayId);
        const activity = await activityService.getActivity(pathway?.parentActivityId);

        if (activity?.studentScopeURN) {
          await interactiveService.authorStudentScopeDeregister(
            this.id,
            childId,
            activity.studentScopeURN
          );
        }
        
        await interactiveService.authorStudentScopeDeregister(
          this.id,
          childId,
        );
      } catch (e) {
        /* If it fails, doesn't matter */
      }
      await componentService.deleteInteractiveComponent(
        childId,
        this.id,
      );
      removeChild(this.id, childId);
    }
    if (this.coursewareType === coursewareTypes.ACTIVITY)  {
      try {
        await activityService.authorStudentScopeDeregister(
          this.id,
          childId,
        );
      } catch (e) {
        /* If it fails, doesn't matter */
      }
      await componentService.deleteActivityComponent(
        childId,
        this.id,
      );
      removeChild(this.id, childId);
    }
  }

  async deleteChildren(childrenIds) {
    const deleteChildrenServiceCalls = [];
    if (this.coursewareType === coursewareTypes.INTERACTIVE) {
      try {
        // Deregister component from activity.
        // This deregister is required because some component's scope are uplifted to the Activity's scope
        // Uplifting are done for components that have scope bindings
        // ie have other courseware elements that depends it for data
        const interactive = await interactiveService.getInteractive(this.id);
        const pathway = await pathwayService.getPathway(interactive?.parentPathwayId);
        const activity = await activityService.getActivity(pathway?.parentActivityId);

        const authorStudentScopeDeregisterWithStudentScopeURN = (id, childId, studentScopeURN) => (async () => interactiveService.authorStudentScopeDeregister(
          id,
          childId,
          studentScopeURN
        ));

        const authorStudentScopeDeregister = (id, childId) => (async () => interactiveService.authorStudentScopeDeregister(
          id,
          childId,
        ));

        for (const childId of childrenIds) {
          if (activity?.studentScopeURN) {
            deleteChildrenServiceCalls.push(authorStudentScopeDeregisterWithStudentScopeURN(this.id, childId, activity.studentScopeURN));
          }
          deleteChildrenServiceCalls.push(authorStudentScopeDeregister(this.id, childId));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('error occured in deleteChildren lifeCycleService of Interactive courseware ', e);
      }

      const deleteInteractiveComponents = async () => componentService.deleteInteractiveComponents(
        childrenIds,
        this.id,
      );

      const _removeChild = (id, childId) => (async () => removeChild(id, childId));

      deleteChildrenServiceCalls.push(deleteInteractiveComponents);

      for (const childId of childrenIds) {
        deleteChildrenServiceCalls.push(_removeChild(this.id, childId));
      }

      await deleteChildrenServiceCalls.reduce(async (previousPromise, nextPromise) => previousPromise.then(() => nextPromise()), Promise.resolve());
    }
    if (this.coursewareType === coursewareTypes.ACTIVITY) {
      try {
        for (const childId of childrenIds) {
          await activityService.authorStudentScopeDeregister(
            this.id,
            childId,
          );
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('error occured in deleteChildren lifeCycleService of Activity courseware', e);
      }
      for (const childId of childrenIds) {
        await componentService.deleteActivityComponent(
          childId,
          this.id,
        );
        removeChild(this.id, childId);
      }
    }
  } 

  async restoreComponents(componentIds) {
    if (this.coursewareType === coursewareTypes.INTERACTIVE) {
      try {
        // Re-Register component to an interactive.
        // This register is required because some component's scope are uplifted to the Activity's scope
        // Uplifting are done for components that have scope bindings
        // ie have other courseware elements that depends it for data
        const restoredComponents = await componentService.restoreInteractiveComponent(
          this.id,
          componentIds
        );

        for (let {id, pluginId, pluginVersion} of restoredComponents) {
          dispatch(
            getPluginSchema(pluginId, pluginVersion, async (schema) => {
              const learnerEditableProperties = findPropertiesByScope(schema);
              if (learnerEditableProperties.length) {
                // Register in the background since it's a slow request
                await interactiveService.authorStudentScopeRegister(
                  this.id,
                  id,
                );
              }
            })
          );
        }

        
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('error occured in restoreComponents', e);
      }
    }
  }

  getAncestry() {
    return coursewareAncestry.get(this.id) || [];
  }

  static build(args) {
    return new LifecycleServices(args);
  }
}

export default LifecycleServices;
